import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Heart, Loader2, MessageCircle, Play, ShoppingBag } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/siteData";
import { api, normalizeProduct, slugify } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";
import CheckoutModal from "@/components/CheckoutModal";

export default function ProductPage() {
  const { slug } = useParams();
  const { brand, contact, categories, razorpay } = useSite();
  const [remote, setRemote] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    setRemote(null);
    setFailed(false);
    setMediaIndex(0);
    let alive = true;
    api
      .get("/products")
      .then((r) => {
        if (alive) setRemote(r.data.map(normalizeProduct));
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const fallback = useMemo(
    () =>
      PRODUCTS.map((p) => ({
        id: String(p.id),
        name: p.name,
        slug: slugify(p.name),
        category: p.category,
        price: p.price,
        description: p.description || "",
        longDescription: p.longDescription || "",
        details: Array.isArray(p.details) ? p.details : [],
        image: p.image || "",
        images: p.image ? [{ path: p.image }] : [],
        video: "",
        alt: p.alt || p.name,
      })),
    [],
  );

  const source = remote || (failed ? fallback : null);
  const product = source?.find((p) => (p.slug || slugify(p.name)) === slug) || null;

  useEffect(() => {
    if (product) document.title = `${product.name} — ${brand.name}`;
  }, [product, brand.name]);

  const images = product?.images?.length ? product.images : product?.image ? [{ path: product.image }] : [];
  const hasVideo = Boolean(product?.video);
  const mediaCount = images.length + (hasVideo ? 1 : 0);
  const showVideo = hasVideo && mediaIndex === images.length;
  const categoryName =
    categories.find((c) => (c.slug || c.id) === (product?.categories?.[0] || product?.category))?.name ||
    CATEGORIES.find((c) => c.id === (product?.categories?.[0] || product?.category))?.name ||
    product?.categories?.[0] ||
    product?.category;
  const canBuy = Boolean(razorpay?.enabled && razorpay?.key_id);

  return (
    <div className="min-h-screen bg-cream font-sans text-ink antialiased">
      <header className="border-b border-line/70 bg-cream/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="product-page-brand-link">
            <Heart size={18} className="fill-rose text-rose" />
            <span className="font-serif text-xl font-semibold">{brand.name}</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-[#5C5C5C] transition-colors hover:text-rose" data-testid="product-page-back-link">
            ← Back to website
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 md:py-20">
        {!source && (
          <div data-testid="product-page-loading" className="flex justify-center py-20">
            <Loader2 size={26} className="animate-spin text-rose" />
          </div>
        )}

        {source && !product && (
          <div data-testid="product-page-not-found" className="py-20 text-center">
            <h1 className="font-serif text-4xl font-medium">Product not found</h1>
            <p className="mt-3 text-sm text-[#5C5C5C]">This gift may have been removed or renamed.</p>
            <Link to="/" className="mt-6 inline-block rounded-full border border-ink px-6 py-3 text-sm font-medium transition-colors hover:bg-ink hover:text-cream" data-testid="product-page-notfound-home">
              Browse all gifts
            </Link>
          </div>
        )}

        {product && (
          <div data-testid="product-page" className="grid gap-10 md:grid-cols-2 md:gap-14">
            <div>
              <div className="overflow-hidden rounded-3xl border border-line bg-white">
                {showVideo ? (
                  <video
                    data-testid="product-page-video-player"
                    src={product.video}
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="aspect-[4/3] w-full bg-ink object-cover"
                  />
                ) : (
                  <img
                    data-testid="product-page-image"
                    src={images[mediaIndex]?.path || product.image}
                    alt={product.alt || product.name}
                    className="aspect-[4/3] w-full object-cover"
                  />
                )}
              </div>
              {mediaCount > 1 && (
                <div data-testid="product-page-thumbs" className="mt-4 flex gap-3 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`product-page-thumb-${i}`}
                      onClick={() => setMediaIndex(i)}
                      aria-label={`View image ${i + 1}`}
                      className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${
                        i === mediaIndex && !showVideo ? "border-rose" : "border-line opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img src={img.path} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                  {hasVideo && (
                    <button
                      data-testid="product-page-thumb-video"
                      onClick={() => setMediaIndex(images.length)}
                      aria-label="Play product video"
                      className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 bg-ink transition-colors ${
                        showVideo ? "border-rose" : "border-line opacity-70 hover:opacity-100"
                      }`}
                    >
                      <Play size={20} className="fill-white text-white" />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-5">
              <span data-testid="product-page-category" className="text-[11px] font-semibold uppercase tracking-[0.25em] text-rose">
                {categoryName}
              </span>
              <h1 data-testid="product-page-name" className="font-serif text-3xl font-medium leading-tight text-ink md:text-5xl">
                {product.name}
              </h1>
              <p data-testid="product-page-price" className="font-serif text-2xl font-semibold text-ink md:text-3xl">
                {product.price}
                <span className="ml-2 align-middle font-sans text-xs font-normal uppercase tracking-[0.15em] text-[#5C5C5C]">
                  incl. packaging
                </span>
              </p>
              <p data-testid="product-page-description" className="whitespace-pre-line text-sm leading-relaxed text-[#5C5C5C] md:text-base">
                {product.description}
              </p>
              {product.longDescription && (
                <p data-testid="product-page-long-description" className="whitespace-pre-line text-sm leading-relaxed text-[#5C5C5C] md:text-base">
                  {product.longDescription}
                </p>
              )}
              {product.details?.length > 0 && (
                <ul className="flex flex-col gap-2.5 border-t border-line pt-5">
                  {product.details.map((d) => (
                    <li key={d} className="flex items-center gap-3 text-sm text-ink/80">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose/10">
                        <Check size={11} className="text-rose" />
                      </span>
                      {d}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                {canBuy && (
                  <button
                    data-testid="product-page-buy"
                    onClick={() => setCheckout(true)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-rose hover:text-white"
                  >
                    <ShoppingBag size={16} /> Buy Now
                  </button>
                )}
                <a
                  data-testid="product-page-whatsapp"
                  href={waLinkFor(contact, `Hello! I'd like to order the ${product.name} (${product.price}).`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#1eb857]"
                >
                  <MessageCircle size={16} /> Order on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </main>

      <CheckoutModal product={checkout ? product : null} onClose={() => setCheckout(false)} />
    </div>
  );
}
